import React, { useCallback, useEffect, useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Button, Drawer } from "antd";
import SprTextMain from "./spr-text-main";
import "./spr-text-drawer.css";

const DRAWER_WIDTH_KEY = "planOnline.sprText.cardDrawer.width";
const MIN_WIDTH = 420;
const MAX_WIDTH = 800;
const DEFAULT_WIDTH = 520;
const SCREEN_MARGIN = 100;

const maxAllowedWidth = () => {
  if (typeof window === "undefined") return MAX_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - SCREEN_MARGIN));
};

const clampWidth = (value) => {
  const max = maxAllowedWidth();
  const n = Number(value);
  if (!Number.isFinite(n)) return Math.min(DEFAULT_WIDTH, max);
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

/**
 * Drawer карточки шаблона (Delphi TFrmESprText).
 */
const SprTextDrawer = ({
  open,
  vid,
  idText,
  idUpper,
  isNew = false,
  hideKod = false,
  canEdit,
  onClose,
  onSaved
}) => {
  const [size, setSize] = useState(loadWidth);

  useEffect(() => {
    if (!open) return undefined;
    const onWinResize = () => setSize((prev) => clampWidth(prev));
    onWinResize();
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, [open]);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const drawerMaxSize = maxAllowedWidth();
  const title = isNew ? "Новый шаблон" : "Шаблон";

  return (
    <Drawer
      placement="right"
      title={title}
      size={size}
      maxSize={drawerMaxSize}
      open={open}
      onClose={onClose}
      destroyOnHidden
      closable={false}
      rootClassName="spr-text-drawer"
      extra={
        <Button
          type="text"
          className="spr-text-drawer__close"
          icon={<CloseOutlined />}
          aria-label="Закрыть"
          title="Закрыть"
          onClick={onClose}
        />
      }
      styles={{
        body: { display: "flex", flexDirection: "column", paddingTop: 12 }
      }}
      resizable={{
        onResize,
        onResizeEnd
      }}
    >
      {open && (idText != null || isNew) ? (
        <SprTextMain
          vid={vid}
          idText={idText}
          idUpper={idUpper}
          isNew={isNew}
          hideKod={hideKod}
          open={open}
          canEdit={canEdit}
          onSaved={onSaved}
          onCancel={onClose}
        />
      ) : null}
    </Drawer>
  );
};

export default SprTextDrawer;
